import React, { Component, ErrorInfo, ReactNode } from 'react';
import { ExclamationTriangleFill, ArrowClockwise } from 'react-bootstrap-icons';

// Import translation helper - we'll use it differently for class components
import type { TranslationKeys } from '../i18n/types';
import { captureBoundaryError } from '../sentry';
import { isChunkLoadError, reloadOnceForChunkError } from '../utils/chunkLoad';

declare global {
  interface Window {
    logError?: (error: Error, errorInfo: ErrorInfo) => void;
    __APP_TEXTS__?: TranslationKeys;
  }
}

interface Props {
  children: ReactNode;
  fallbackComponent?: React.ComponentType<{ error: Error; errorInfo: ErrorInfo | null }>;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Reload the page once when a code-split chunk failed to load (see utils/chunkLoad.ts). */
  reloadOnChunkError?: boolean;
}

interface State {
  hasError: boolean;
  reloading?: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log to external service if available
    if (window.logError) {
      window.logError(error, errorInfo);
    }
    captureBoundaryError(error, errorInfo.componentStack);

    if (this.props.reloadOnChunkError && isChunkLoadError(error) && reloadOnceForChunkError()) {
      this.setState({ reloading: true });
    }
  }

  override render() {
    if (this.state.reloading) {
      return null;
    }
    if (this.state.hasError) {
      // Use custom fallback component if provided
      if (this.props.fallbackComponent) {
        const FallbackComponent = this.props.fallbackComponent;
        return <FallbackComponent error={this.state.error!} errorInfo={this.state.errorInfo} />;
      }

      // Get translations from window object (set by AppContext)
      const t = window.__APP_TEXTS__;
      const errorText = {
        title: t?.errors?.something_went_wrong || 'Something went wrong',
        message: t?.errors?.unexpected_error || 'We encountered an unexpected error. Please try refreshing the page or contact support if the problem persists.',
        refresh: t?.errors?.refresh_page || 'Refresh Page',
        tryAgain: t?.errors?.try_again || 'Try Again',
        details: t?.errors?.error_details || 'Error Details (Development)'
      };

      // Default fallback UI
      return (
        <div className="container py-4">
          <div className="alert alert-danger" role="alert">
            <div className="d-flex align-items-center mb-3">
              <ExclamationTriangleFill className="text-danger me-2" />
              <h4 className="alert-heading mb-0">{errorText.title}</h4>
            </div>
            <p className="mb-3">
              {errorText.message}
            </p>
            <div className="d-flex gap-2">
              <button 
                className="btn btn-outline-danger"
                onClick={() => window.location.reload()}
              >
                <ArrowClockwise className="me-1" />
                {errorText.refresh}
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => this.setState({ hasError: false, error: null, errorInfo: null })}
              >
                {errorText.tryAgain}
              </button>
            </div>
            {process.env['NODE_ENV'] === 'development' && this.state.error && (
              <details className="mt-3">
                <summary className="text-muted">{errorText.details}</summary>
                <pre className="mt-2 small text-muted">
                  {this.state.error.stack}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;